import { NumColumn } from './dataset.js';
import { aggregate, buildGroupsMulti } from './query.js';
import { parseDate, displayDate } from './format.js';
import { colName, excelDate } from './xlsx-core.js';
import { setHtml } from './css.js';
import { chartFromRange } from './chart-core.js';
import { SideBar } from './sidebar.js';
import { CardView } from './cardview.js';
import { EXTRA, CHECKISH, headerChains, download, colAggs } from './grid-util.js';
import { normValidation } from './input.js';
import { RowState } from './dataset.js';
// ── 1.2.0 지연 로딩: 엑셀(xlsx)·PDF(+인쇄 HTML)·차트 그리기·디자이너는 처음 쓸 때 import() (코어 묶음에서 빠짐). 한 번 불러오면 모듈을 기억해 다음부터 바로 씀
let chartMod = null;
let xlsxMod = null;
let designerMod = null;
/** @internal */ export const lazy = {
    xlsx: () => import('./xlsx.js').then(m => (xlsxMod = m)),
    pdf: () => import('./pdf.js'),
    chart: () => import('./chart.js').then(m => (chartMod = m)),
    designer: () => import('./designer.js').then(m => (designerMod = m)),
};
export class GridExport {
    // ── 인쇄 지원 ──
    printColumns(ids) {
        const cs = ids ? ids.map(id => this.cols.find(c => c.id === id && !c.special)).filter((c) => !!c) : this.cols.filter(c => !c.special);
        return cs.map(c => ({ id: c.id, header: c.def.header ?? c.id, width: c.width, align: this.alignOf(c) }));
    }
    printRows(max) {
        const cs = this.cols.filter(c => !c.special);
        const out = [];
        let n = 0;
        for (let vi = 0; vi < this.disp.length && n < max; vi++) {
            const it = this.disp[vi];
            if (it <= EXTRA)
                continue;
            if (it < 0) {
                const g = this.groups[-it - 1];
                out.push({ kind: 'group', label: `${g.label} (${g.count ?? this.liveRows(g.rows).length})`, level: g.level });
                continue;
            }
            if (this.opts.showDeletedRows && this.ds.state[it] === RowState.DELETE)
                continue; // 삭제 행 표시 중이어도 인쇄에는 안 넣음
            n++;
            out.push({ kind: 'row', cells: cs.map(c => { const t = this.displayText(c, this.valueOf(c, it), it); const lv = this.opts.tree && c.id === this.treeLabelCol() ? '\u3000'.repeat(this.getTreeLevel(it)) : ''; return lv + t; }) });
        }
        return out;
    }
    printFooter(ids) {
        return ids.map((id, i) => { const c = this.colInfo(id); return c.def.summary || c.def.summaryExpr ? this.summaryText(c, this.liveRows(this.view)) : i === 0 ? this.opts.footerLabel ?? '' : ''; });
    }
    /** @internal */ alignOf(c) {
        const a = c.def.align;
        if (a === 'center')
            return 'c';
        if (a === 'right' || (a == null && !c.def.items && (this.ds.cols[c.id]?.kind === 'num' || c.def.format === 'number' || c.def.format === 'money')))
            return 'r';
        return 'l';
    }
    /** 선택 범위 → 차트 창 (우클릭 "차트로 보기…"). 첫 컬럼이 글자면 이름, 아니면 행 머리(그리드의 첫 글자 컬럼) */
    openRangeChart(type = 'bar') {
        this.closeRangeChart();
        const r = this.range();
        const cols = this.cols.slice(r.c0, r.c1 + 1).filter(c => !c.special);
        const rows = Array.from(this.disp.slice(r.r0, r.r1 + 1)).filter(x => x >= 0);
        if (!cols.length || !rows.length)
            return;
        const lc = this.cols.find(c => !c.special && !(this.ds.cols[c.id] instanceof NumColumn));
        const rowLabels = lc && !cols.includes(lc) ? rows.map(row => this.cellText(row, lc.id)) : undefined;
        const spec = chartFromRange(cols.map(c => c.def.header ?? c.id), rows.map(row => cols.map(c => this.cellValue(row, c.id))), rows.map(row => cols.map(c => this.cellText(row, c.id))), { type, rowLabels });
        const p = document.createElement('div');
        p.className = 'og-chartpop';
        p.setAttribute('role', 'dialog');
        p.setAttribute('aria-label', this.t('선택 범위 차트'));
        const T = [['bar', this.t('막대')], ['line', '선'], ['pie', this.t('원형')]];
        setHtml(p, `<div class="og-undohh"><b>${this.t('선택 범위 차트 ({rows}행 × {series}계열)', { rows: rows.length, series: spec.series.length })}</b><span>${T.map(([t, l]) => `<button type="button" class="og-cht" data-t="${t}" aria-pressed="${t === type}">${l}</button>`).join('')}<button type="button" data-a="x" aria-label="${this.t('닫기')}">×</button></span></div><div class="og-chartb"></div>`);
        this.chartEl = p;
        this.root.appendChild(p);
        const body = p.querySelector('.og-chartb');
        const draw = (t) => {
            p.querySelectorAll('.og-cht').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.t === t)));
            if (!spec.series.length) {
                body.textContent = this.t('숫자 값이 없습니다');
                return;
            }
            const go = (m) => { if (this.chartEl === p)
                m.renderChart(body, { ...spec, type: t, series: t === 'pie' ? spec.series.slice(0, 1) : spec.series, height: 260 }); };
            if (chartMod)
                go(chartMod);
            else {
                body.textContent = this.t('불러오는 중…');
                lazy.chart().then(go, err => { body.textContent = String(err?.message ?? err); });
            }
        };
        p.addEventListener('click', e => {
            const b = e.target.closest('button');
            if (!b)
                return;
            if (b.dataset.a === 'x') {
                this.closeRangeChart();
                this.focusGrid();
                return;
            }
            if (b.dataset.t)
                draw(b.dataset.t);
        });
        p.addEventListener('keydown', e => { if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeRangeChart();
            this.focusGrid();
        } });
        draw(type);
        p.querySelector(`.og-cht[data-t="${type}"]`)?.focus();
    }
    closeRangeChart() { this.chartEl?.remove(); this.chartEl = null; }
    /** PDF 파일로 내려받기 (인쇄 옵션 그대로 — 쪽마다 이미지, 외부 라이브러리 없음). 쪽 수를 돌려줌 */
    async exportPdf(filename = 'export.pdf', o = {}) {
        const { gridToPdf } = await lazy.pdf();
        const r = await gridToPdf(this, o);
        download(r.pdf, filename, 'application/pdf');
        return { pages: r.pages, bytes: r.pdf.length };
    }
    /**
     * 서버 엑셀 내보내기 요청 본문 (지금 보이는 컬럼 순서·머리글·너비·형식 + 정렬·필터·빠른 검색). 계산 컬럼(value·expr·valueGetter)·단추 셀은 뺌.
     * 서버(Spring XlsxExport)는 등록된 컬럼만 받고 나머지는 무시한다
     */
    serverExportSpec(opts = {}) {
        const q = this.getServerQuery();
        const cols = this.cols.filter(c => !c.special && !c.def.value && !c.def.valueGetter && c.def.cellType !== 'button' && this.ds.hasColumn(c.id)).map(c => {
            const d = c.def, kind = this.ds.cols[c.id]?.type;
            return { id: c.id, header: d.header ?? c.id, width: Math.round(c.width), type: kind ?? 'string', align: d.align ?? null,
                format: typeof d.format === 'string' ? d.format : null, decimals: d.decimals ?? null, privacy: d.privacy ?? null };
        });
        return { columns: cols, sort: q.sort, filter: q.filter, quick: q.quick || null, sheetName: opts.sheetName ?? null, title: opts.title ?? null };
    }
    /**
     * 서버에서 큰 엑셀 만들기 (Spring SXSSF 스트리밍, 100만 행). 지금 보기 상태(serverExportSpec)를 POST 하고 받은 파일을 내려받는다.
     * 받는 동안 로딩 막에 받은 크기 표시. 반환: { bytes, ms, rows(서버 X-Row-Count) }
     */
    async exportServerXlsx(url, opts = {}) {
        const t0 = performance.now();
        const fileName = opts.fileName ?? 'export.xlsx';
        this.setLoading(this.t('엑셀 만드는 중…'));
        try {
            const res = await fetch(url, { method: 'POST', ...opts.init, headers: { 'Content-Type': 'application/json', Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ...(opts.init?.headers ?? {}) }, body: JSON.stringify({ ...this.serverExportSpec(opts), fileName }) });
            if (!res.ok) {
                const b = await res.json().catch(() => null);
                throw new Error(b?.message ?? `HTTP ${res.status}`);
            }
            const rowsH = res.headers.get('X-Row-Count');
            const parts = [];
            let n = 0, last = 0;
            const reader = res.body?.getReader();
            if (reader) {
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done)
                        break;
                    parts.push(value);
                    n += value.length;
                    const now = performance.now();
                    if (now - last > 150) {
                        last = now;
                        this.setLoading(this.t('엑셀 받는 중 {n}', { n: n >= 1048576 ? (n / 1048576).toFixed(1) + 'MB' : Math.ceil(n / 1024) + 'KB' }));
                        opts.onProgress?.(n);
                    }
                }
            }
            else {
                const b = await res.arrayBuffer();
                parts.push(b);
                n = b.byteLength;
            }
            opts.onProgress?.(n);
            if (opts.download !== false) {
                const blob = new Blob(parts, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const u = URL.createObjectURL(blob), a = document.createElement('a');
                a.href = u;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(u), 5000);
            }
            return { bytes: n, ms: performance.now() - t0, rows: rowsH == null ? null : Number(rowsH), fileName };
        }
        finally {
            this.setLoading(false);
        }
    }
    exportXlsx(filename = 'export.xlsx', sheetName) {
        const sheet = this.exportSheet(sheetName); // 지금 화면 상태로 (불러오는 동안 바뀌지 않게 먼저)
        const go = (m) => download(m.buildXlsx(sheet), filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        if (xlsxMod) {
            go(xlsxMod);
            return Promise.resolve();
        } // 한 번 불러왔으면 예전처럼 바로
        return lazy.xlsx().then(go);
    }
    /**
     * 서식 있는 엑셀 시트 데이터: 2단 헤더(병합), 셀 병합·반복 숨김, 그룹 머리행(수준 들여쓰기·윤곽) + 소계 행,
     * 숫자 형식, 컬럼 너비, 조건부 글자색(cellClass → excelClassStyles), 틀 고정(헤더 + 왼쪽 고정 컬럼)
     */
    exportSheet(sheetName) {
        const cs = this.cols.filter(c => !c.special);
        const styles = [], styleKey = new Map();
        const sty = (x) => { const k = JSON.stringify(x); let i = styleKey.get(k); if (i == null) {
            i = styles.length;
            styles.push(x);
            styleKey.set(k, i);
        } return i; };
        const numFmtOf = (c) => {
            const d = c.def, num = this.ds.cols[c.id]?.kind === 'num' || !!d.value;
            if (!num || d.items || d.privacy || typeof d.format === 'function')
                return undefined;
            const dec = d.decimals ?? (d.format === 'money' ? 0 : undefined);
            if (d.format === 'number' || d.format === 'money')
                return dec ? '#,##0.' + '0'.repeat(dec) : '#,##0';
            return dec ? '0.' + '0'.repeat(dec) : undefined;
        };
        const colFmt = cs.map(numFmtOf);
        const colStyles = colFmt.map(f => (f ? sty({ numFmt: f }) : null));
        const classSt = this.opts.excelClassStyles ?? {};
        // 헤더: 그룹 줄 + 컬럼 줄, 그룹 없는 컬럼은 위아래 병합
        const merges = [];
        const { chains: hch } = headerChains(this.opts.headerGroups);
        const D = cs.reduce((m, c) => Math.max(m, hch.get(c.id)?.length ?? 0), 0);
        let headerRows;
        let headers;
        if (D) {
            // (D+1)줄 머리글 격자: 그룹 단계 줄 + 컬럼 줄. 그룹 얕은 컬럼은 세로 병합, 같은 그룹 연속 칸은 가로 병합
            const H = Array.from({ length: D + 1 }, () => cs.map(() => null));
            cs.forEach((c, i) => {
                const ch = hch.get(c.id) ?? [], k = ch.length;
                H[k][i] = c.def.header ?? c.id;
                if (k < D)
                    merges.push(`${colName(i)}${k + 1}:${colName(i)}${D + 1}`);
            });
            for (let L = 0; L < D; L++)
                for (let i = 0; i < cs.length; i++) {
                    const g = hch.get(cs[i].id)?.[L];
                    if (!g)
                        continue;
                    let j = i;
                    while (j + 1 < cs.length && hch.get(cs[j + 1].id)?.[L] === g)
                        j++;
                    H[L][i] = g.header;
                    if (j > i)
                        merges.push(`${colName(i)}${L + 1}:${colName(j)}${L + 1}`);
                    i = j;
                }
            headerRows = H.slice(0, D);
            headers = H[D];
        }
        else
            headers = cs.map(c => c.def.header ?? c.id);
        const nh = (headerRows?.length ?? 0) + 1;
        const rows = [], rowStyles = [], cellStyles = [], outline = [];
        const cellOf = (c, r) => { const v = this.valueOf(c, r); return typeof v === 'number' && !c.def.privacy && !c.def.items && typeof c.def.format !== 'function' ? v : this.displayText(c, v, r) || null; };
        const prevRow = []; // 데이터 행 순서 (병합·반복 숨김 비교)
        // 셀 메모 → 엑셀 메모 (excelNotes: false 면 뺌)
        const notes = [], withNotes = this.opts.excelNotes !== false && this.ds.notes.size > 0;
        const mergeCols = cs.map((c, i) => (this.mergeActive(c) ? i : -1)).filter(i => i >= 0);
        const mergeStart = new Map(); // 컬럼 → 현재 병합 시작 시트 행
        const closeMerges = (endSheetRow, only) => {
            for (const [ci, st] of mergeStart) {
                if (only != null && only !== ci)
                    continue;
                if (endSheetRow > st)
                    merges.push(`${colName(ci)}${st}:${colName(ci)}${endSheetRow}`);
                mergeStart.delete(ci);
            }
        };
        const pushData = (r, level) => {
            const sheetRow = nh + rows.length + 1, prev = prevRow.length ? prevRow[prevRow.length - 1] : -1;
            const vals = cs.map(c => cellOf(c, r));
            const cst = colStyles.slice();
            cs.forEach((c, i) => {
                const cls = c.def.cellClass?.(this.ds.get(r, c.id), r, this.ds);
                if (cls)
                    for (const k of cls.split(/\s+/))
                        if (classSt[k])
                            cst[i] = sty({ ...classSt[k], numFmt: colFmt[i] });
                if (c.def.suppress && prev >= 0 && this.valueOf(c, prev) === this.valueOf(c, r))
                    vals[i] = null;
            });
            for (const ci of mergeCols) {
                const same = prev >= 0 && this.sameMerge(cs[ci], prev, r);
                if (same) {
                    if (!mergeStart.has(ci))
                        mergeStart.set(ci, sheetRow - 1);
                }
                else
                    closeMerges(sheetRow - 1, ci);
                if (same)
                    vals[ci] = null;
            }
            if (withNotes)
                cs.forEach((c, i) => { const n = this.ds.getNote(r, c.id); if (n?.note)
                    notes.push({ row: rows.length, col: i, text: n.note, author: n.userId }); });
            prevRow.push(r);
            rows.push(vals);
            cellStyles.push(cst);
            rowStyles.push(null);
            outline.push(level);
        };
        const gcols = this.groupCols();
        if (gcols.length && this.groupArgs) {
            const full = buildGroupsMulti(this.ds, this.liveRows(this.view), this.groupArgs.levels, () => false, this.groupArgs.order);
            const gSt = (lv) => sty({ bold: true, fill: lv ? 'F3F6F9' : 'E8EEF4', indent: lv });
            const subSt = cs.map((_, i) => sty({ bold: true, fill: 'FBF7E8', numFmt: colFmt[i] }));
            const stack = [];
            const closeGroup = (g) => {
                closeMerges(nh + rows.length);
                const vals = cs.map((c, i) => {
                    if (i === 0)
                        return `${'  '.repeat(g.level)}소계: ${g.label}`;
                    const fn = colAggs(c.def)[0];
                    if (!fn)
                        return null;
                    const v = c.def.value ? this.aggComputed(c, g.rows, fn) : this.ds.cols[c.id] ? aggregate(this.ds, g.rows, c.id, fn) : null;
                    return typeof v === 'number' ? v : v == null ? null : String(v);
                });
                rows.push(vals);
                cellStyles.push(subSt.slice());
                rowStyles.push(null);
                outline.push(g.level + 1);
                prevRow.push(-1);
            };
            for (let i = 0; i < full.disp.length; i++) {
                const it = full.disp[i];
                if (it >= 0) {
                    pushData(it, stack.length);
                    continue;
                }
                const g = full.groups[-it - 1];
                while (stack.length && stack[stack.length - 1].level >= g.level)
                    closeGroup(stack.pop());
                closeMerges(nh + rows.length);
                const header = g.col ? this.headerOf(g.col) : '';
                rows.push([`${header ? header + ': ' : ''}${g.label} (${g.count ?? g.rows.length})`, ...cs.slice(1).map(() => null)]);
                cellStyles.push(cs.map(() => gSt(g.level)));
                rowStyles.push(null);
                outline.push(g.level);
                prevRow.push(-1);
                stack.push(g);
            }
            while (stack.length)
                closeGroup(stack.pop());
        }
        else {
            const lv = this.liveRows(this.view);
            for (let i = 0; i < lv.length; i++)
                pushData(lv[i], 0);
        }
        closeMerges(nh + rows.length);
        // prevRow 의 -1(그룹·소계 행) 다음 데이터는 병합·반복 숨김을 새로 시작하도록 위에서 prev 를 -1 로 둠
        const pinnedLeft = cs.filter(c => c.pane === 0).length;
        return {
            name: sheetName, headers, headerRows, merges, styles, colStyles, cellStyles, rowStyles, outline,
            rows, widths: cs.map(c => Math.round(c.width / 7)), freeze: { rows: nh, cols: pinnedLeft }, notes: notes.length ? notes : undefined,
        };
    }
    exportCsv(filename = 'export.csv') {
        const { headers, rows } = this.exportMatrix(false);
        // CSV 수식 주입 방지: =,+,-,@ 로 시작하는 글자(숫자 아님)는 앞에 ' 를 붙여 엑셀이 수식으로 실행하지 않게
        const q = (v) => { let s = v == null ? '' : String(v); if (/^[=+\-@\t\r]/.test(s) && !/^[-+]?[\d,.]+%?$/.test(s))
            s = "'" + s; return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
        const text = '﻿' + [headers, ...rows].map(r => r.map(q).join(',')).join('\r\n');
        download(new TextEncoder().encode(text), filename, 'text/csv;charset=utf-8');
    }
    /** 가져온 엑셀 헤더를 컬럼 id 로 (헤더 글자 또는 id 가 같으면, extra 로 별칭 지정) */
    mapImportHeaders(headers, extra = {}) {
        const norm = (t) => t.replace(/[\s*]/g, '').toLowerCase();
        return headers.map(h => {
            if (extra[h])
                return extra[h];
            const c = this.cols.find(c => !c.special && !c.def.value && (norm(c.def.header ?? '') === norm(h) || c.id.toLowerCase() === norm(h)));
            return c ? c.id : null;
        });
    }
    /**
     * 엑셀 등에서 읽은 행을 새 행(INSERT)으로 붙인다. mapping[i] = 원본 i 번째 값의 컬럼 id.
     * 콤보는 코드·명칭 모두 받고, 날짜 일련번호·체크 값을 바꾼 뒤 검증한다. 반환: 추가 행·오류
     */
    importRows(rows, mapping) {
        this.commitEdit();
        const added = [], convErr = [];
        this.ds.batch(() => {
            for (const src of rows) {
                const vals = {}, bad = [];
                mapping.forEach((col, i) => {
                    if (!col)
                        return;
                    const c = this.colInfo(col);
                    if (!c)
                        return;
                    let v = src[i] ?? null;
                    if (v === '' || v == null) {
                        vals[col] = null;
                        return;
                    }
                    const dc = this.ds.cols[col], kind = this.editorOf(c);
                    if (c.def.items || kind === 'combo') {
                        const p = this.parseCombo(c, String(v));
                        if ('value' in p)
                            v = p.value;
                        else
                            bad.push({ col, message: `목록에 없는 값: ${v}` });
                    }
                    else if (CHECKISH.has(kind)) {
                        const cv = this.checkValues(c);
                        v = /^(1|y|true|v|o|✓)$/i.test(String(v)) ? cv[0] : cv[1];
                    }
                    else if (dc?.type === 'date' || c.def.format === 'date' || kind === 'date')
                        v = typeof v === 'number' ? excelDate(v) : parseDate(String(v)) ?? v;
                    else if (dc?.kind === 'num' && typeof v === 'string') {
                        const n = Number(v.replace(/,/g, ''));
                        if (n === n)
                            v = n;
                        else
                            bad.push({ col, message: `숫자가 아닙니다: ${v}` });
                    }
                    vals[col] = v;
                });
                const nv = typeof this.opts.newRow === 'function' ? this.opts.newRow() : {};
                // 값 쓰기 전 (source 'import', 새로 생길 행 번호) → 행 추가 전
                const nr = this.ds.length;
                const kept = this.beforeChanges(Object.entries(vals).map(([col, value]) => ({ row: nr, col, value })), 'import', false); // 바이트 초과는 넣고 validate 가 오류 표시
                const iv = this.beforeRowInsert(this.view.length + added.length, { ...nv, ...Object.fromEntries(kept.map(k => [k.col, k.value])) }, 'import');
                if (!iv)
                    continue;
                const r = this.ds.addRow(iv);
                added.push(r);
                for (const b of bad)
                    convErr.push({ row: r, ...b });
            }
        });
        const errs = this.validate().filter(e => added.includes(e.row));
        for (const e of convErr) {
            this.errors.set(e.row + ':' + e.col, e.message);
            errs.push(e);
        }
        this.bump();
        return { added, errors: errs };
    }
    /** 내보내기용 행렬 (현재 필터·정렬 순서, 표시 형식) */
    exportMatrix(rawNumbers) {
        const cs = this.cols.filter(c => !c.special);
        const headers = cs.map(c => c.def.header ?? c.id);
        const view = this.liveRows(this.view); // 삭제 행 표시 중이어도 내보내기에는 안 넣음
        const rows = new Array(view.length);
        for (let i = 0; i < view.length; i++) {
            const r = view[i];
            rows[i] = cs.map(c => {
                const v = this.valueOf(c, r);
                if (rawNumbers && typeof v === 'number' && !c.def.privacy)
                    return v;
                return this.displayText(c, v, r);
            });
        }
        return { headers, rows, widths: cs.map(c => Math.round(c.width / 7)) };
    }
    /** 보기 형식 바꾸기 (그리드 ↔ 카드). 정렬·필터·그룹 결과(그룹 머리행 제외)와 포커스 행은 그대로 */
    setView(v) {
        this.opts.view = v;
        if (v === 'card') {
            if (this.editing)
                this.cancelEdit();
            if (!this.cardView)
                this.cardView = new CardView(this, this.root);
            this.cardView.refresh();
            this.cardView.focus();
        }
        else if (this.cardView) {
            const row = this.cardView.currentRow();
            this.cardView.destroy();
            this.cardView = null;
            if (row >= 0)
                this.focusCell(row);
            else
                this.focusGrid();
        }
    }
    getView() { return this.cardView ? 'card' : 'grid'; }
    /** @internal */ schedCardRefresh() { if (this.cardRefreshQ)
        return; this.cardRefreshQ = true; queueMicrotask(() => { this.cardRefreshQ = false; this.cardView?.refresh(); }); }
    /** 화면 표시 목록 — 0 이상 = 물리 행, -1… = 그룹 머리행(groupInfo), 그보다 작은 값 = 그룹 푸터 등 (카드 뷰용) */
    getDisplayItems() { return this.isGrouped() ? this.disp : this.view; }
    /** 이 셀의 편집기 종류 — 지금 편집할 수 없으면 null (카드 뷰가 체크·라디오·콤보를 고를 때) */
    editorKind(row, col) { const c = this.colInfo(col); return c && this.canEditCol(c, row) ? this.editorOf(c, row) : null; }
    /** 체크·라디오 셀이 켜졌나 (3상태 가운데 값이면 null) */
    isCellChecked(row, col) {
        const c = this.colInfo(col);
        if (!c)
            return false;
        const v = this.ds.get(row, col), [on, , mid] = this.checkValues3(c);
        return v === on || v === true ? true : this.editorOf(c, row) === 'check3' && v === mid ? null : false;
    }
    /** 체크·라디오 셀 바꾸기 (그리드에서 누른 것과 같음: 라디오 그룹·실행 취소). 바꿨으면 true */
    toggleCell(row, col) {
        const c = this.colInfo(col);
        if (!c || !this.canEditCol(c, row) || !CHECKISH.has(this.editorOf(c, row)))
            return false;
        this.toggleCheckCell(c, row);
        return true;
    }
    /** 콤보 목록 (행별 규칙 포함) */
    cellItems(row, col) { const c = this.colInfo(col); return c ? this.itemsOf(c, row) : []; }
    /** 값 그대로 검증·저장(실행 취소 포함). 오류면 메시지, 성공이면 null — 카드 뷰 콤보 편집이 씀 */
    editCellValue(row, col, value) {
        const c = this.colInfo(col);
        if (!c || !this.canEditCol(c, row))
            return this.t('편집할 수 없는 칸입니다');
        const vr = normValidation(c.def.validate?.(value, row, this.ds));
        if (vr?.level === 'error')
            return vr.message;
        const be = this.bytesError(c, value);
        if (be)
            return be;
        this.setCellByUser([{ row, col, value }], this.t('편집'), 'edit', { noBuffer: true });
        this.afterCellValidate(row, col, vr);
        return null;
    }
    /** 화면 컬럼 (특수 칸 제외) — 카드 뷰·외부 도구용 */
    visibleColumns() { return this.cols.filter(c => !c.special).map(c => ({ id: c.id, header: c.def.header ?? c.id, align: this.alignOf(c) })); }
    /** 이 셀을 지금 편집할 수 있나 (편집 가능·권한·보호·규칙·onBeforeEdit) */
    canEditCell(row, col) { const c = this.colInfo(col); if (!c || !this.canEditCol(c, row))
        return false; const k = this.editorOf(c, row); return !!k && !CHECKISH.has(k); }
    /** 편집 글자 → 값 해석·검증·저장(실행 취소 포함). 오류면 메시지, 성공이면 null — 카드 뷰 편집이 씀 */
    editCellText(row, col, text) {
        const c = this.colInfo(col);
        if (!c || !this.canEditCol(c, row))
            return this.t('편집할 수 없는 칸입니다');
        const kind = this.editorOf(c, row);
        const p = kind === 'combo' ? this.parseCombo(c, text) : this.parseInput(c, text, kind, row);
        if ('error' in p)
            return p.error;
        const vr = normValidation(c.def.validate?.(p.value, row, this.ds));
        if (vr?.level === 'error')
            return vr.message;
        this.setCellByUser([{ row, col, value: p.value }], this.t('편집'), 'edit', { noBuffer: true });
        this.afterCellValidate(row, col, vr);
        return null;
    }
    /** 편집 시작 글자 (콤보는 이름, 날짜는 표시 형식) */
    editText(row, col) {
        const c = this.colInfo(col);
        if (!c)
            return '';
        const v = this.valueOf(c, row), kind = this.editorOf(c, row);
        return v == null ? '' : kind === 'combo' ? this.comboMap(c).get(String(v))?.label ?? String(v) : kind === 'date' ? displayDate(v) : String(v);
    }
    /** 옆 도구 패널 열기·닫기 */
    openSideBar(tab = 'columns') { if (!this.sideBar)
        this.sideBar = new SideBar(this, this.root, this.t); this.sideBar.open(tab); }
    closeSideBar() { this.sideBar?.close(); }
    /** 그리드 설정 창 (개발자·편집자용 속성 편집기) */
    /** 그리드 설정 창 (1.2.0: 처음 열 때 디자이너 모듈을 불러옴 — Promise) */
    openDesigner() {
        if (designerMod) {
            designerMod.openGridDesigner(this);
            return Promise.resolve();
        } // 불러온 뒤에는 바로 (예전처럼 동기)
        return lazy.designer().then(m => m.openGridDesigner(this));
    }
}
